import AuthRepository from "../../../components/repository/AuthRepository"
import { pageReload } from "../../../util/PageReload"
import MerchantSettingKey from "../../Common/Enum/MerchantSettingKey"
import PinLogin from "../PinLogin/PinLogin"

const LOCAL_STORAGE_KEY_AUTH_CONTROLLER = "AUTH_CONTROLLER"

const AuthData = {
    currentUser: {
        id: window.merchant_admin_id
    },
    pinLogin: {
        isLoggedIn: true,
    },
}

class PlaceholderUser {
    id;
    isNotFromFirebase = true;

    constructor(id) {
        this.id = id;
    }
}

class AuthController {
    currentUser = null
    #loginPinHash = null
    #loginPinSalt = null

    /**
     * @var PinLogin
     */
    #pinLogin = PinLogin

    /**
     * @var MerchantApp
     */
    #app = null

    #authData = AuthData

    #firebaseRef = null

    // So that we can use object as key to store listener
    listenerWeakMap = new Map()

    // The actual listener functions are stored here
    listenerMap = new Map()

    isPinLoginEnabled = () => {
        return this.#app && this.#app.getMerchantSettingValue(MerchantSettingKey.PIN_LOGIN_MODULE_ENABLED) == 1
    }

    constructor() {
        // Init when page load
        this.currentUser = new PlaceholderUser(window.merchant_admin_id)
        this.#loginPinHash = window.login_pin_hash
        this.#loginPinSalt = window.login_pin_salt
        this.#authData = this.#getData()
        window.AuthController = this

        addEventListener("storage", (event) => {
            if (event.key == LOCAL_STORAGE_KEY_AUTH_CONTROLLER) {
                this.#authData = this.#getData()
                this.notifyAllListeners()
                this.#pinLogin.notifyAllListeners()
            }
        })
    }
    init = (app) => {
        this.#app = app
        this.initCount = (this.initCount || 0) + 1
        this.loadFirebaseData()
        PinLogin.init(app)
        return this
    }
    setCurrentUser(merchantAdmin, loginPinHash, loginPinSalt) {
        // We will store loginPinHash & loginPinSalt for login purpose, however
        // merchantAdmin (if changed) shall be loaded from Firebase to pickup realtime changes
        this.#loginPinHash = loginPinHash
        this.#loginPinSalt = loginPinSalt

        if (merchantAdmin.id != this.currentUser.id) {
            this.currentUser = Object.assign(new PlaceholderUser(merchantAdmin.id), merchantAdmin)
            this.loadFirebaseData()
        }
        else {
            this.currentUser = merchantAdmin
            this.#authData.currentUser.id = merchantAdmin.id
            this.#setData(this.#authData)
            this.notifyAllListeners()
        }
    }
    getLoginPinHashAndSalt = (merchantAdminId) => {
        let res = { loginPinHash: null, loginPinSalt: null }
        if (this.currentUser.id == merchantAdminId) {
            res.loginPinHash = this.#loginPinHash
            res.loginPinSalt = this.#loginPinSalt
        }
        return res
    }

    #getData = (extra = {}) => {
        return Object.assign(AuthData, JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_AUTH_CONTROLLER) || "{}") , extra)
    }
    #setData = (data) => {
        localStorage.setItem(LOCAL_STORAGE_KEY_AUTH_CONTROLLER, JSON.stringify(data))
    }

    getAuthData = () => {
        return this.#authData
    }
    pinLogin = async (merchantAdminId, pin) => {
        if (merchantAdminId == this.currentUser.id) {
            // Current relogin to continue session
            if (await this.checkCurrentUserPin(pin)) {
                this.#authData.pinLogin.isLoggedIn = true
                console.log("this.#authData.pinLogin.isLoggedIn", this.#authData.pinLogin.isLoggedIn)
                this.#setData(this.#authData)
                return this.#successPinLoginResult()
            }
            return Promise.reject("Wrong PIN")
        }
        else {
            // Login as other user
            return AuthRepository.pinLogin(merchantAdminId, pin)
                .then((res) => {
                    this.#authData.pinLogin.isLoggedIn = true
                    this.setCurrentUser(res.merchant_admin, res.login_pin_hash, res.login_pin_salt)
                    return this.#successPinLoginResult()
                })
        }
    }
    pinLogout = () => {
        this.#authData.pinLogin.isLoggedIn = false
        this.#setData(this.#authData)
        return Promise.resolve(this.#successPinLoginResult())
    }
    #successPinLoginResult = () => {
        return {isLoggedIn: this.#authData.pinLogin.isLoggedIn}
    }
    loadFirebaseData = () => {
        if (this.#firebaseRef) {
            firebase.database().ref(firebase_path + `/merchant_admins`).off('value', this.#firebaseRef)
        }
        const firebaseRefId = this.initCount
        this.#firebaseRef = firebase.database().ref(firebase_path + `/merchant_admins`)
            .orderByChild("id")
            .equalTo(parseInt(this.currentUser.id))
            .on('value', snapshot => {
                console.log("Firebase value", firebaseRefId)
                let currentMerchantAdmin = this.currentUser
                if (!(currentMerchantAdmin instanceof PlaceholderUser)) {
                    // Default login merchant changed due to merchant change company, either from
                    // this tab or from other tab (other machine)
                    pageReload.reload()
                    return
                }
                let merchantAdmins = []
                snapshot.forEach(childSnapshot => {
                    let item = childSnapshot.val()
                    item.key = childSnapshot.key
                    merchantAdmins.push(item)
                })

                if (merchantAdmins.length) {
                    window.access_level = merchantAdmins[0].access_level
                    const { loginPinHash, loginPinSalt } = this.getLoginPinHashAndSalt(merchantAdmins[0].id)
                    this.setCurrentUser(merchantAdmins[0], loginPinHash, loginPinSalt)
                }
            })
    }
    checkCurrentUserPin = async (pin) => {
        return await this.sha1(this.#loginPinSalt + pin) === this.#loginPinHash
    }
    sha1 = async (str) => {
        const enc = new TextEncoder();
        const hash = await crypto.subtle.digest('SHA-1', enc.encode(str));
        return Array.from(new Uint8Array(hash))
            .map(v => v.toString(16).padStart(2, '0'))
            .join('');
    }
    notifyListener = (callback, type) => {
        if (type === 'pin_login') {
            callback(this.#pinLogin)
        }
        else if (type === 'user_change') {
            callback(this.currentUser)
        }
    }
    notifyAllListeners = () => {
        // For each context, get all the listeners
        for (let [key, listeners] of this.listenerMap) {
            // Fire all the listeners based on their type
            Object.keys(listeners).forEach(type => {
                this.notifyListener(listeners[type], type)
            })
        }
    }
    addCurrentUserChangeListener = (context, callback) => {
        let listeners = this.listenerMap.get(context) || {}
        listeners['user_change'] = callback
        this.listenerMap.set(context, listeners)
        setTimeout(() => {
            this.notifyListener(callback, 'user_change')
        }, 100)

        // For chaining
        return this
    }
    addPinLoginChangeListener = (context, callback) => {
        this.#pinLogin.addOnChangeListener(context, callback)

        // For chaining
        return this
    }
    removeListeners = (context) => {
        this.listenerMap.delete(context)
        this.#pinLogin.removeOnChangeListener(context)
    }

    /**
     * Revalidate the login state, update internal states, and notify listeners of the change
     * @var PinLogin
     */
    refresh = () => {
        this.#pinLogin.refresh()
    }

    /**
     * Check current user pin, if matched, allow pin access for 5 minutes
     * @param {string} pin - Current user pin for checking
     */
    allowPinAccessFor5Minute = async (pin) => {
        if (await this.checkCurrentUserPin(pin)) {
            this.isPinAccessValid = true
            this.#setClearPinValidityTimer(0.2)
            return true
        }
        return false
    }
    #setClearPinValidityTimer = (minute, action) => {
        if (this.clearPinValidityTimer) {
            clearTimeout(this.clearPinValidityTimer)
        }

        let timeInterval = minute * 60 * 1000
        if (action == 'extend') {
            if (this.pinAccessValidUntil) {
                let newExpireTime = moment().add(minute, 'm')
                if (newExpireTime.isAfter(this.pinAccessValidUntil)) {
                    // Adjust the time interval to account for the time difference
                    timeInterval = newExpireTime.diff(moment(), 'ms')
                    this.pinAccessValidUntil = newExpireTime
                }
                else {
                    // Do nothing, use the original expiry time
                    timeInterval = null
                }
            }
            else {
                this.pinAccessValidUntil = moment().add(minute, 'm')
            }   
        }
        
        if (timeInterval) {
            this.clearPinValidityTimer = setTimeout(() => {
                this.isPinAccessValid = false
                this.pinAccessValidUntil = null
                console.log("Pin access expired")
            }, timeInterval)
        }
    }
    isPinAuthorized = () => {
        return !!this.isPinAccessValid
    }
    /**
     * Extend the pin access time for up to N minutes. If original expiry time is longer than the 
     * desired time, then this function does nothing.
     * @param {int} minute 
     */
    extendPinAccess = (minute) => {
        if (this.isPinAccessValid) {
            this.#setClearPinValidityTimer(minute, 'extend')
        }
        throw new PinAccessExpireError()
    }
}

// https://javascript.info/custom-errors
export class PinAccessExpireError extends Error {
    constructor(message = "Pin access already expired") {
        super(message); // (1)
        this.name = "PinAccessExpireError"; // (2)
      }
}
export default new AuthController